import { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, Power } from "lucide-react";

const TYPES = ["connection_note", "first_pitch", "follow_up_1", "follow_up_2", "follow_up_3", "follow_up_4", "objection_response"];
const TIERS = ["All", "Award-Signal", "Momentum Builder", "Established Operator", "No Budget", "No Paid PR", "Low Traffic", "No ROI"];

export default function Templates() {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState({ type: "first_pitch", tier_target: "All", body_text: "" });

  const load = () => api.get("/templates").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (t, body) => {
    try { await api.patch(`/templates/${t.id}`, { body_text: body }); toast.success(`Saved v${(t.version || 1) + 1}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const toggle = async (t) => { await api.patch(`/templates/${t.id}`, { active: !t.active }); load(); };
  const del = async (t) => { await api.delete(`/templates/${t.id}`); toast.success("Deleted"); load(); };
  const create = async () => {
    if (!creating.body_text.trim()) { toast.error("Body required"); return; }
    try { await api.post("/templates", creating); toast.success("Template created"); setCreating({ ...creating, body_text: "" }); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="font-head text-2xl font-extrabold tracking-tight mb-1">Template Library</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Versioned scripts the generator pulls from. Use {"{name} {company} {city} {title}"} placeholders.</p>

      <div className="bg-white border-2 border-black p-4 mb-6" data-testid="template-create">
        <div className="text-xs font-bold uppercase tracking-wider mb-2">New template</div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <select value={creating.type} onChange={(e) => setCreating({ ...creating, type: e.target.value })} className="border border-zinc-400 px-2 py-2 text-sm" data-testid="new-tmpl-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={creating.tier_target} onChange={(e) => setCreating({ ...creating, tier_target: e.target.value })} className="border border-zinc-400 px-2 py-2 text-sm" data-testid="new-tmpl-tier">
            {TIERS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <textarea value={creating.body_text} onChange={(e) => setCreating({ ...creating, body_text: e.target.value })} rows={3} placeholder="Message body…"
          className="w-full border border-zinc-400 p-2 text-sm resize-none focus:border-klein focus:outline-none" data-testid="new-tmpl-body" />
        <button onClick={create} className="mt-2 flex items-center gap-2 bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium transition-colors" data-testid="create-tmpl-btn">
          <Plus className="h-4 w-4" /> Add template
        </button>
      </div>

      <div className="space-y-3">
        {list.map((t) => <Row key={t.id} t={t} onSave={save} onToggle={toggle} onDel={del} />)}
      </div>
    </div>
  );
}

function Row({ t, onSave, onToggle, onDel }) {
  const [body, setBody] = useState(t.body_text);
  useEffect(() => setBody(t.body_text), [t.body_text]);
  return (
    <div className={`bg-white border p-4 ${t.active ? "border-zinc-300" : "border-zinc-200 opacity-60"}`} data-testid={`tmpl-${t.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono bg-zinc-900 text-white px-2 py-0.5">{t.type}</span>
          <span className="text-xs font-mono border border-zinc-400 px-2 py-0.5">{t.tier_target}</span>
          <span className="text-[10px] text-zinc-400 font-mono">v{t.version}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onToggle(t)} title="Toggle active" className="text-zinc-500 hover:text-klein"><Power className="h-4 w-4" /></button>
          <button onClick={() => onDel(t)} className="text-zinc-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} className="w-full border border-zinc-300 p-2 text-sm resize-none focus:border-klein focus:outline-none" />
      {body !== t.body_text && (
        <button onClick={() => onSave(t, body)} className="mt-2 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 text-xs font-medium transition-colors">
          <Save className="h-3.5 w-3.5" /> Save new version
        </button>
      )}
    </div>
  );
}
